import { z } from "zod";

export const cpfSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ""))
  .pipe(
    z.string().refine((v) => !v || (v.length === 11 && isValidCpf(v)), {
      message: "clients_validation_cpf",
    }),
  )
  .describe("CPF: strips mask; empty string allowed; validates check digits when present.");

export const cnpjSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ""))
  .pipe(
    z.string().refine((v) => !v || (v.length === 14 && isValidCnpj(v)), {
      message: "clients_validation_cnpj",
    }),
  )
  .describe("CNPJ: strips mask; empty string allowed; validates check digits when present.");

export const requiredCpfSchema = z
  .string({ message: "clients_validation_cpf_required" })
  .transform((v) => v.replace(/\D/g, ""))
  .pipe(
    z
      .string()
      .refine((v) => v.length === 11 && isValidCpf(v), { message: "clients_validation_cpf" }),
  )
  .describe("CPF: strips mask; required; validates check digits.");

export const requiredCnpjSchema = z
  .string({ message: "clients_validation_cnpj_required" })
  .transform((v) => v.replace(/\D/g, ""))
  .pipe(
    z
      .string()
      .refine((v) => v.length === 14 && isValidCnpj(v), { message: "clients_validation_cnpj" }),
  )
  .describe("CNPJ: strips mask; required; validates check digits.");

export const formatTaxId = (value: string): string => {
  const digits = stripTaxIdDigits(value).slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const CPF_DIGITS_REGEX = /^\d{11}$/;
const CNPJ_DIGITS_REGEX = /^\d{14}$/;

const CNPJ_CHECK_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const CNPJ_CHECK_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

const stripTaxIdDigits = (value: string): string => value.replace(/\D/g, "");

const isRepeatedDigits = (digits: string): boolean => /^(\d)\1+$/.test(digits);

const calcMod11CheckDigit = (digits: string, weights: readonly number[]): number => {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += Number(digits[i]) * weights[i]!;
  }
  const remainder = sum % 11;

  return remainder < 2 ? 0 : 11 - remainder;
};

const calcCpfCheckDigit = (digits: string, length: number): number => {
  let sum = 0;
  for (let i = 0; i < length; i++) {
    sum += Number(digits[i]) * (length + 1 - i);
  }
  const remainder = sum % 11;

  return remainder < 2 ? 0 : 11 - remainder;
};

const isValidCpf = (digits: string): boolean => {
  if (!CPF_DIGITS_REGEX.test(digits) || isRepeatedDigits(digits)) return false;

  const firstCheck = calcCpfCheckDigit(digits, 9);
  if (firstCheck !== Number(digits[9])) return false;

  const secondCheck = calcCpfCheckDigit(digits, 10);

  return secondCheck === Number(digits[10]);
};

const isValidCnpj = (digits: string): boolean => {
  if (!CNPJ_DIGITS_REGEX.test(digits) || isRepeatedDigits(digits)) return false;

  const firstCheck = calcMod11CheckDigit(digits, CNPJ_CHECK_WEIGHTS_1);
  if (firstCheck !== Number(digits[12])) return false;

  const secondCheck = calcMod11CheckDigit(digits, CNPJ_CHECK_WEIGHTS_2);

  return secondCheck === Number(digits[13]);
};
